import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";

export default function BackfillProcessingFees() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBackfill = async () => {
    setIsProcessing(true);
    setError(null);
    setResults(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('backfill-processing-fees-v2', {
        body: {},
      });

      if (invokeError) {
        throw invokeError;
      }

      setResults(data?.results || data);
      console.log('Backfill results:', data);
    } catch (err) {
      console.error('Backfill error:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle>Backfill Processing Fees</CardTitle>
          <CardDescription>
            This tool will calculate and update missing processing fees for all customer_permits and site_bookings records where processing_fee = 0.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This process will:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Find all customer_permits with processing_fee = 0 and payment_status = 'paid'</li>
              <li>Find all site_bookings with processing_fee = 0 and payment_status = 'paid'</li>
              <li>Calculate the correct processing fee using the 8-tier structure</li>
              <li>Update processing_fee and total_price = subtotal + processing_fee</li>
            </ul>
          </div>

          <Button 
            onClick={handleBackfill} 
            disabled={isProcessing}
            className="w-full"
          >
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isProcessing ? 'Processing...' : 'Start Backfill'}
          </Button>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {results && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Backfill Complete!</p>
                  {results.permits && (
                    <div className="text-sm space-y-1 mt-2">
                      <p className="font-medium">Customer Permits:</p>
                      <p>• Processed: {results.permits.processed}</p>
                      <p>• Updated: {results.permits.updated}</p>
                      <p>• Skipped: {results.permits.skipped}</p>
                      {results.permits.errors?.length > 0 && (
                        <p className="text-destructive">• Errors: {results.permits.errors.length}</p>
                      )}
                    </div>
                  )}
                  {results.bookings && (
                    <div className="text-sm space-y-1 mt-2">
                      <p className="font-medium">Site Bookings:</p>
                      <p>• Processed: {results.bookings.processed}</p>
                      <p>• Updated: {results.bookings.updated}</p>
                      <p>• Skipped: {results.bookings.skipped}</p>
                      {results.bookings.errors?.length > 0 && (
                        <p className="text-destructive">• Errors: {results.bookings.errors.length}</p>
                      )}
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
